import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from '../../hooks/use-toast';
import {
  MapPin,
  Package,
  Truck,
  User,
  Phone,
  Calendar,
  Clock,
  ChevronRight,
  CheckCircle2,
  XCircle,
  Navigation,
  Upload,
  FileText,
  Wallet,
  RefreshCw,
  ExternalLink,
  Loader2
} from 'lucide-react';
import { formatMerchandiseType, formatVehicleType } from '../../utils/formatters';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MissionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [mission, setMission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [actionNote, setActionNote] = useState('');
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadType, setUploadType] = useState('bon_chargement');
  const [uploadComment, setUploadComment] = useState('');

  const fetchMission = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/driver/missions/${id}`,
        {}
      );
      setMission(response.data);
    } catch (error) {
      console.error('Error fetching mission:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger la mission',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchMission();
  }, [fetchMission]);

  const handleAccept = async () => {
    try {
      setActionLoading(true);
      await axios.post(
        `${API}/driver/missions/${id}/accept`,
        {},
        {}
      );
      toast({ title: 'Succès', description: 'Mission acceptée' });
      fetchMission();
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Erreur lors de l\'acceptation',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleRefuse = async () => {
    try {
      setActionLoading(true);
      await axios.post(
        `${API}/driver/missions/${id}/refuse`,
        { reason: actionNote || 'Mission refusée par le chauffeur' },
        {}
      );
      toast({ title: 'Mission refusée', description: 'La mission a été refusée' });
      navigate('/chauffeur/missions');
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Erreur lors du refus',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(false);
      setShowActionDialog(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      setActionLoading(true);
      await axios.post(
        `${API}/driver/missions/${id}/status`,
        { status: newStatus, note: actionNote || undefined },
        {}
      );
      toast({ title: 'Succès', description: 'Statut mis à jour' });
      fetchMission();
      setShowActionDialog(false);
      setActionNote('');
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Erreur lors de la mise à jour',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      toast({ title: 'Erreur', description: 'Veuillez sélectionner un fichier', variant: 'destructive' });
      return;
    }

    try {
      setActionLoading(true);
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('documentType', uploadType);
      if (uploadComment) formData.append('comment', uploadComment);

      await axios.post(
        `${API}/driver/missions/${id}/documents`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      toast({ title: 'Succès', description: 'Document envoyé' });
      setShowUploadDialog(false);
      setUploadFile(null);
      setUploadComment('');
      fetchMission();
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Erreur lors de l\'envoi',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const openNavigation = () => {
    if (mission?.arrivalCoordinates) {
      const { lat, lng } = mission.arrivalCoordinates;
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank');
    } else if (mission?.arrivalAddress) {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(mission.arrivalAddress)}`, '_blank');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      planifiee: { label: 'Planifiée', class: 'bg-blue-100 text-blue-800' },
      prete_au_chargement: { label: 'Au chargement', class: 'bg-teal-100 text-teal-800' },
      en_cours_de_chargement: { label: 'Chargement', class: 'bg-orange-100 text-orange-800' },
      charge_en_route: { label: 'En route', class: 'bg-emerald-100 text-emerald-800' },
      livree: { label: 'Livrée', class: 'bg-green-100 text-green-800' }
    };
    const config = statusConfig[status] || { label: status, class: 'bg-gray-100 text-gray-800' };
    return <Badge className={`text-sm ${config.class}`}>{config.label}</Badge>;
  };

  const getNextAction = () => {
    if (!mission) return null;
    
    if (mission.status === 'planifiee' && !mission.driverConfirmed) {
      return { label: 'Accepter la mission', action: handleAccept, color: 'bg-[#1db584]' };
    }
    if (mission.status === 'planifiee' && mission.driverConfirmed) {
      return { label: 'Arrivé au chargement', action: () => { setActionType('prete_au_chargement'); setShowActionDialog(true); }, color: 'bg-teal-600' };
    }
    if (mission.status === 'prete_au_chargement') {
      return { label: 'Chargement terminé', action: () => { setActionType('en_cours_de_chargement'); setShowActionDialog(true); }, color: 'bg-orange-600' };
    }
    if (mission.status === 'en_cours_de_chargement') {
      return { label: 'Départ - En route', action: () => { setActionType('charge_en_route'); setShowActionDialog(true); }, color: 'bg-blue-600' };
    }
    if (mission.status === 'charge_en_route') {
      return { label: 'Confirmer la livraison', action: () => { setActionType('livree'); setShowActionDialog(true); }, color: 'bg-green-600' };
    }
    return null;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount || 0) + ' FCFA';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[#1db584]" />
      </div>
    );
  }

  if (!mission) {
    return (
      <Card className="p-8 text-center">
        <p className="text-gray-500">Mission non trouvée</p>
      </Card>
    );
  }

  const nextAction = getNextAction();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-lg md:text-2xl font-bold text-gray-900">{mission.operationNumber}</h1>
          <p className="text-gray-600">{mission.departure} → {mission.arrival}</p>
        </div>
        {getStatusBadge(mission.status)}
      </div>

      {/* Progress Steps */}
      <Card className="p-4">
        <div className="flex items-center justify-between">
          {[
            { key: 'planifiee', label: 'Assigné', icon: Clock },
            { key: 'prete_au_chargement', label: 'Arrivé', icon: MapPin },
            { key: 'en_cours_de_chargement', label: 'Chargé', icon: Package },
            { key: 'charge_en_route', label: 'En route', icon: Navigation },
            { key: 'livree', label: 'Livré', icon: CheckCircle2 }
          ].map((step, index, arr) => {
            const statusOrder = ['planifiee', 'prete_au_chargement', 'en_cours_de_chargement', 'charge_en_route', 'livree'];
            const currentIndex = statusOrder.indexOf(mission.status);
            const stepIndex = statusOrder.indexOf(step.key);
            const isCompleted = stepIndex <= currentIndex;
            const isCurrent = stepIndex === currentIndex;
            const Icon = step.icon;

            return (
              <React.Fragment key={step.key}>
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    isCompleted ? 'bg-[#1db584] text-white' : 'bg-gray-200 text-gray-400'
                  } ${isCurrent ? 'ring-4 ring-[#1db584]/30' : ''}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <span className={`text-xs mt-1 ${isCompleted ? 'text-[#1db584] font-medium' : 'text-gray-400'}`}>
                    {step.label}
                  </span>
                </div>
                {index < arr.length - 1 && (
                  <div className={`flex-1 h-1 mx-2 rounded ${stepIndex < currentIndex ? 'bg-[#1db584]' : 'bg-gray-200'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </Card>

      {/* Main Action Button */}
      {nextAction && (
        <Button
          onClick={nextAction.action}
          className={`w-full h-14 text-lg ${nextAction.color} hover:opacity-90`}
          disabled={actionLoading}
        >
          {actionLoading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
          {nextAction.label}
        </Button>
      )}

      {/* Refuse button for pending missions */}
      {mission.status === 'planifiee' && !mission.driverConfirmed && (
        <Button
          variant="outline"
          onClick={() => { setActionType('refuse'); setShowActionDialog(true); }}
          className="w-full border-red-300 text-red-600 hover:bg-red-50"
        >
          <XCircle className="w-4 h-4 mr-2" />
          Refuser la mission
        </Button>
      )}

      {/* Route Info */}
      <Card className="p-4">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <MapPin className="w-5 h-5 text-[#1db584]" />
          Itinéraire
        </h3>
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-3 h-3 rounded-full bg-green-500 mt-1.5" />
            <div>
              <p className="font-medium">{mission.departure}</p>
              <p className="text-sm text-gray-500">{mission.departureAddress}</p>
            </div>
          </div>
          <div className="ml-1.5 border-l-2 border-dashed border-gray-300 h-6" />
          <div className="flex items-start gap-3">
            <div className="w-3 h-3 rounded-full bg-red-500 mt-1.5" />
            <div>
              <p className="font-medium">{mission.arrival}</p>
              <p className="text-sm text-gray-500">{mission.arrivalAddress}</p>
            </div>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={openNavigation}
          className="w-full mt-4"
        >
          <ExternalLink className="w-4 h-4 mr-2" />
          Lancer la navigation GPS
        </Button>
      </Card>

      {/* Details Grid */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="p-4">
          <h4 className="text-sm text-gray-500 mb-1">Marchandise</h4>
          <p className="font-semibold">{formatMerchandiseType(mission.merchandiseType)}</p>
          <p className="text-sm text-gray-600">{mission.weight} tonnes</p>
        </Card>
        <Card className="p-4">
          <h4 className="text-sm text-gray-500 mb-1">Véhicule</h4>
          <p className="font-semibold">{mission.assignedVehicle?.plate || 'N/A'}</p>
          <p className="text-sm text-gray-600">{formatVehicleType(mission.assignedVehicle?.type || mission.vehicleType)}</p>
        </Card>
        <Card className="p-4">
          <h4 className="text-sm text-gray-500 mb-1">Chargement prévu</h4>
          <p className="font-semibold flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            {mission.scheduledLoadingDate 
              ? new Date(mission.scheduledLoadingDate).toLocaleDateString('fr-FR')
              : 'Non défini'}
          </p>
        </Card>
        <Card className="p-4">
          <h4 className="text-sm text-gray-500 mb-1">Rémunération</h4>
          <p className="font-semibold text-[#1db584] flex items-center gap-1">
            <Wallet className="w-4 h-4" />
            {formatCurrency(mission.driverPayment)}
          </p>
        </Card>
      </div>

      {/* Client Contact */}
      <Card className="p-4">
        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <User className="w-5 h-5 text-[#1db584]" />
          Contact Client
        </h3>
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium">{mission.clientContact?.name || 'N/A'}</p>
            <p className="text-sm text-gray-500">{mission.clientContact?.phone}</p>
          </div>
          {mission.clientContact?.phone && (
            <a href={`tel:${mission.clientContact.phone}`}>
              <Button size="sm" variant="outline">
                <Phone className="w-4 h-4 mr-2" />
                Appeler
              </Button>
            </a>
          )}
        </div>
      </Card>

      {/* Documents */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#1db584]" />
            Documents
          </h3>
          <Button size="sm" onClick={() => setShowUploadDialog(true)}>
            <Upload className="w-4 h-4 mr-2" />
            Ajouter
          </Button>
        </div>
        {mission.documents?.length > 0 ? (
          <div className="space-y-2">
            {mission.documents.map((doc, index) => (
              <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                <span className="text-sm">{doc.type} - {doc.fileName}</span>
                <span className="text-xs text-gray-500">
                  {new Date(doc.uploadedAt).toLocaleDateString('fr-FR')}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">Aucun document</p>
        )}
      </Card>

      {/* Action Dialog */}
      <Dialog open={showActionDialog} onOpenChange={setShowActionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === 'refuse' ? 'Refuser la mission ?' : 'Confirmer l\'action'}
            </DialogTitle>
            <DialogDescription>
              {actionType === 'refuse' 
                ? 'Veuillez indiquer la raison du refus (optionnel)'
                : 'Ajoutez une note si nécessaire'}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Note (optionnel)..."
            value={actionNote}
            onChange={(e) => setActionNote(e.target.value)}
            rows={3}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActionDialog(false)}>
              Annuler
            </Button>
            <Button
              onClick={() => actionType === 'refuse' ? handleRefuse() : handleStatusChange(actionType)}
              disabled={actionLoading}
              className={actionType === 'refuse' ? 'bg-red-600 hover:bg-red-700' : 'bg-[#1db584]'}
            >
              {actionLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Envoyer un document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Type de document</label>
              <select
                value={uploadType}
                onChange={(e) => setUploadType(e.target.value)}
                className="w-full mt-1 p-2 border rounded-md"
              >
                <option value="bon_chargement">Bon de chargement</option>
                <option value="bon_livraison">Bon de livraison</option>
                <option value="photo_marchandise">Photo marchandise</option>
                <option value="autre">Autre</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Fichier</label>
              <input
                type="file"
                accept="image/*,.pdf"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                className="w-full mt-1"
              />
            </div>
            <Textarea
              placeholder="Commentaire (optionnel)..."
              value={uploadComment}
              onChange={(e) => setUploadComment(e.target.value)}
              rows={2}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleUpload} disabled={actionLoading || !uploadFile}>
              {actionLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MissionDetail;
